import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import useEditableLayers from "../../../hooks/useEditableLayers";
import useLayersChange from "../../../hooks/useLayersChange";
import { setCycleManagerOpen } from "../../../redux/action/CycleManager-action";
import {
  setEditableLayer,
  setEditorUpdateType,
} from "../../../redux/action/Dashboard-action";
import { ConfigContext } from "../../../utils/ConfigContext";
import { getLayerSymbology } from "../../../utils/symbologies";
import CustomSwiper, { CustomSwiperSlide } from "../../CustomSwiper";
import { BaselineIcon, SharedInformationIcon } from "../../Icons";
import ProgressBar from "../../Report/new/ProgressBar/ProgressBar";
import SharedInformation from "../../SharedInformation/SharedInformation";
import { Button, Tooltip } from "../../UI";
import CycleManagerProgress from "../CycleManager/CycleManagerProgress/CycleManagerProgress";
import {
  StyledTitle as EditorTitle,
  StyledEditorBreadcrumbs,
  StyledEditorManagerContainer,
  StyledEditorWrapper,
  StyledListContainer,
} from "./Editor-styled";
import EditorContextProvider, { UpdateType } from "./EditorContextProvider";
import EditorMetrics from "./EditorMetrics/EditorMetrics";
import EditorPlatform from "./EditorPlatform";
import { EditType } from "./EditorSwiper/EditorSwiper";
import NotificationMenu, { NotificationPage } from "./NotificationMenu";
import Platform from "./Platform/Platform";
import {
  PlatformIconHolder,
  PlatformSectionItem,
  PlatformSectionItemBody,
} from "./Platform/Platform-styled";

const reportManager = {
  id: "reportManager",
  step: "reportManager",
  editType: "",
  label: "Shared Information",
  icon: SharedInformationIcon,
  tag: 3,
};

const EditorManagerSelection = ({
  handleNext,
  situationalLayers,
  entryPointsLayers,
  baseLineLayers,
  activeModule,
}) => {
  const open = useSelector((state) => state.cycleManagerReducer.open);
  const reportsLayerMap = useSelector(
    (state) => state.dashboard.reportsLayerMap
  );
  const reports = useSelector((state) => state.dashboard.reports);
  const reportsLayer = useSelector((state) => state.dashboard.reportsLayer);
  const { config } = useContext(ConfigContext);
  const { t } = useTranslation("common");
  const { layerCount, visibleLayersIds } = useLayersChange(config);
  // const [sharedInformation, setSharedInformation] = useState({
  //   show: false,
  //   updates: 0,
  // });
  const [reportColors, setReportColor] = useState({});

  const dispatch = useDispatch();

  const timer = useRef(0);

  useEffect(() => {
    if (Object.keys(reportColors).length > 0 || !reportsLayer) return;

    if (reportsLayer) {
      const colors = {};
      if (Object.keys(colors).length === 0) {
        const { colorMap = {} } = getLayerSymbology(reportsLayer, config) || {};
        if (colorMap.default) {
          Object.keys(colorMap.default).forEach((code) => {
            const color = colorMap.default[code];
            colors[code] = color;
          });
        }
      }
      setReportColor(colors);
    }
  }, [reportsLayer]);


  const handleLayerSelection = useCallback(
    (layer) => {
      if (layer) {
        dispatch(setEditableLayer(layer));
      }

      handleNext("platform");
    },
    [handleNext]
  );

  const filteredBaselineLayers = useMemo(() => {
    const configLayers = config.layers || [];
    if (config.disableBaselineEditor) return [];

    const { layers: moduleLayers = [], optionalLayers = [] } =
      config.modules[activeModule] || {};

    const layersToShow = baseLineLayers.filter(
      (l) =>
        moduleLayers.includes(l.layerConfig?.alias) ||
        optionalLayers.includes(l.layerConfig?.alias) ||
        configLayers.includes(l.layerConfig?.alias)
    );

    return layersToShow;
  }, [config, activeModule, baseLineLayers]);

  if (open) return null;

  if (!config.newEditorEnabled) {
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <StyledEditorBreadcrumbs hasProgress>
          <ProgressBar color={config.opsColor} currentPosition={0} total={1} />
          <p>Editor Manager</p>
        </StyledEditorBreadcrumbs>
        <div
          style={{
            textAlign: "left",
            padding: "10px 14px",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CycleManagerProgress config={config} />
          <EditorTitle>What do you want to do?</EditorTitle>
          {/* <StyledList>
            {allSelections.map((item) => (
              <StyledListItem
                onClick={() => handleNext(item.step, item.editType)}
                key={item.id}
              >
                {Number.isFinite(item.tag) && (
                  <div
                    style={{
                      position: "absolute",
                      right: 0,
                      top: -10,
                    }}
                  >
                    <div className="tag">{item.tag} NEW</div>
                  </div>
                )}

                {<item.icon fill="#c0392b" />}
                <span>{item.label}</span>
              </StyledListItem>
            ))}
          </StyledList> */}
        </div>
      </div>
    );
  } else {
    return (
      <>
        <div style={{ textAlign: "center" }}>
          <span
            style={{
              fontSize: "17px",
              color: "var(--fontColor)",
              display: "inline-block",
              textWrap: "nowrap",
              lineHeight: "normal",
            }}
          >
            {t("screen.widget.Editor.manager.title")}
          </span>
        </div>
        <div style={{ textAlign: "center", marginTop: 16, marginBottom: 4 }}>
          <span
            style={{
              fontSize: "15px",
              color: "var(--fontColor)",
              display: "inline-block",
              textWrap: "nowrap",
              lineHeight: "normal",
            }}
          >
            {t("screen.widget.Editor.manager.situational.title")}
          </span>
        </div>
        <StyledEditorManagerContainer
          style={{ padding: "0px 0px 10px 0px", gap: 0 }}
        >
          <div
            style={{
              padding: "0px 0px 10px 0px",
            }}
          >
            {situationalLayers.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <EditorMetrics />
                <StyledListContainer>
                  <Platform
                    layers={situationalLayers}
                    entryPointsLayers={entryPointsLayers}
                    onLayerSelect={handleLayerSelection}
                    updateType={UpdateType.situational}
                    reportsLayerMap={reportsLayerMap}
                    allReports={reports}
                    reportColors={reportColors}
                  />
                </StyledListContainer>
              </div>
            )}
            <div
              style={{
                display: "flex",
                padding: "0px 14px",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {config.allowPushNotifications && (
                <Button
                  onClick={() => handleNext("notificationMenuStep")}
                  style={{ width: "100%", position: "relative" }}
                  isNew
                >
                  <Tooltip
                    showOnHover
                    placement="top"
                    content={t(
                      "screen.widget.Editor.notifications.information"
                    )}
                    absolute
                    blurred
                  />
                  <NotificationMenu />
                  {t("screen.widget.Editor.notifications.headerTitle")}
                </Button>
              )}
            </div>
          </div>
          {filteredBaselineLayers.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: 10,
                flexDirection: "column",
                padding: "10px 0px",
              }}
            >
              <div style={{ textAlign: "center", marginTop: 0 }}>
                <span
                  style={{
                    fontSize: "15px",
                    color: "var(--fontColor)",
                    display: "inline-block",
                    textWrap: "nowrap",
                    lineHeight: "normal",
                  }}
                >
                  {t("screen.widget.Editor.manager.baseline.title")}
                </span>
              </div>
              <div
                style={{
                  padding: "0px 14px",
                }}
              >
                <PlatformSectionItem
                  onClick={() => {
                    dispatch(setEditorUpdateType(UpdateType.baseline));
                    handleLayerSelection();
                  }}
                >
                  <Tooltip
                    placement="top"
                    content={t(
                      "screen.widget.Editor.manager.baseline.information"
                    )}
                    showOnHover
                    absolute
                    wrapperStyles={{
                      zIndex: 2,
                    }}
                    blurred
                  />
                  <PlatformIconHolder color="transparent">
                    <BaselineIcon fill="rgb(73, 73, 73)" />
                  </PlatformIconHolder>
                  <PlatformSectionItemBody>
                    <span className="title">
                      {t("screen.widget.Editor.manager.baseline.workOnDataset")}
                    </span>
                  </PlatformSectionItemBody>
                </PlatformSectionItem>
              </div>
            </div>
          )}
        </StyledEditorManagerContainer>
      </>
    );
  }
};

const editorManager = {
  editorManager: 0,
  reportManager: 1,
  platform: 2,
  notificationMenuStep: 4,
};

const EditorManager = ({ expand, setEditingActive }) => {
  const [step, setStep] = useState("editorManager");
  const [editType, setEditType] = useState(EditType.edit);
  const swiperRef = useRef();
  const activeModule = useSelector((state) => state.activeModule);
  const { editableFeature } = useSelector((state) => state.dashboard);
  const { config } = useContext(ConfigContext);
  const {
    editableLayers,
    situationalLayers,
    entryPointsLayers,
    baselineLayers,
  } = useEditableLayers(config, false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (step !== "editorManager") {
      setEditingActive(true);
    } else {
      setEditingActive(false);
    }
  }, [step]);

  useEffect(() => {
    if (step !== "editorManager") {
      handleSlideChange("editorManager");
    }
  }, [activeModule]);

  //for editor
  useEffect(() => {
    if (!config.newEditorEnabled) return;
    if (editableFeature && step !== "platform") {
      setTimeout(() => {
        const layer = editableFeature.sourceLayer
          ? editableFeature.sourceLayer
          : editableFeature.layer;
        handleSlideChange("platform", EditType.edit, 0);
        dispatch(setEditableLayer(layer));
      }, 500);
    }
  }, [editableFeature]);

  const handleSlideChange = useCallback((id, editType = "", speed = 300) => {
    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.slideTo(editorManager[id], speed);
    }

    if (editType) {
      setEditType(editType);
    }
    setStep(id);
  }, []);

  useEffect(() => {
    if (!expand && !!step) {
      swiperRef.current?.swiper?.slideTo(editorManager["editorManager"]);
      setStep("editorManager");
    }
  }, [expand]);

  const handleGoBack = useCallback(
    (slide) => {
      handleSlideChange(typeof slide === "string" ? slide : "editorManager");
      dispatch(setCycleManagerOpen(false));
    },
    [handleSlideChange]
  );

  if (!expand) return;

  return (
    <StyledEditorWrapper>
      <CustomSwiper
        ref={swiperRef}
        init="true"
        initial-slide={0}
        prevent-interaction-on-transition
        no-swiping
        allow-touch-move={false}
        prevent-clicks={false}
        simulate-touch={false}
        prevent-clicks-propagation={false}
        resizeObserver={true}
        observer={true}
        virtual="true"
        style={{
          width: "100%",
          height: "100%",
        }}
      >
        <CustomSwiperSlide key="selection">
          {expand && step === "editorManager" && (
            <EditorManagerSelection
              handleNext={handleSlideChange}
              situationalLayers={situationalLayers}
              entryPointsLayers={entryPointsLayers}
              baseLineLayers={baselineLayers}
              activeModule={activeModule}
            />
          )}
        </CustomSwiperSlide>
        <CustomSwiperSlide key="reportManager">
          {step === "reportManager" && (
            <SharedInformation
              expand={true}
              selectedTab="sharedInformation"
              handleGoBack={handleGoBack}
            />
          )}
        </CustomSwiperSlide>
        <CustomSwiperSlide key="platform">
          {step === "platform" && (
            <EditorContextProvider editType={editType}>
              <EditorPlatform
                setEditType={setEditType}
                handleGoBack={handleGoBack}
                editType={editType}
                editableLayers={editableLayers}
                baselineLayers={baselineLayers}
                entryPointsLayers={entryPointsLayers}
                situationalLayers={situationalLayers}
              />
            </EditorContextProvider>
          )}
        </CustomSwiperSlide>
        {config.allowPushNotifications && (
          <CustomSwiperSlide key="notificationMenuStep">
            {step === "notificationMenuStep" && (
              <NotificationPage goBack={handleGoBack} />
            )}
          </CustomSwiperSlide>
        )}
      </CustomSwiper>
    </StyledEditorWrapper>
  );
};

export default EditorManager;
